import { api, ServiceName } from './api';
import {
  CustomizedBlockPagePayload,
  Group,
  GroupItem,
  IFlagsConfig,
  IPremiumDetailsResponse,
  SaveUserConfigPayload,
  UserConfig,
} from '@/models';
import { logger } from '@/utils';
import { LOGGER_ACTION, LOGGER_FEATURE } from '@/constants/logger/logger-tags';

class ApiUser {
  /**
   * Check if an email already exists in the system
   * @param email - The email to check
   * @returns true if email exists, false otherwise
   */
  public async checkIfEmailExists(email: string): Promise<boolean> {
    try {
      const response = await api.httpGet<{ exists: boolean }>(
        ServiceName.UserManagement,
        '/a7f3b2c1',
        { a7f3b2c1: email }
      );
      return response.exists;
    } catch (e) {
      logger.error('Failed to check if email exists', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.CHECK_EMAIL_EXISTS,
        },
      });
      return false;
    }
  }

  public async getPremiumDetails(): Promise<
    IPremiumDetailsResponse | undefined
  > {
    try {
      return await api.httpGet<IPremiumDetailsResponse>(
        ServiceName.PremiumHub,
        '/premium'
      );
    } catch (e) {
      logger.error('Failed to get premium details', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.GET_PREMIUM_DETAILS,
        },
      });
      return undefined;
    }
  }

  public async mergeUsersInBraze(
    aliases: { alias: string; label: string }[]
  ): Promise<boolean> {
    try {
      await api.httpPost(
        ServiceName.UserState,
        '/internal-events/braze/merge-users',
        aliases
      );
      return true;
    } catch (e) {
      logger.error('Failed to merge users in Braze', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.MERGE_USERS_IN_BRAZE,
        },
      });
      return false;
    }
  }

  public async getUserConfig(): Promise<UserConfig> {
    return await api.httpGet<UserConfig>(ServiceName.UserState, '/user-config');
  }

  /**
   * Get a custom token from UserManagement using the extension's ID token.
   * Backend uses the token to identify the user (req.userId) and returns a custom token.
   * Use this when signing in from the extension (e.g. incognito).
   */
  public async getCustomToken(idToken: string): Promise<string> {
    const response = await api.httpGet<string>(
      ServiceName.UserManagement,
      '/getCustomToken',
      { Authorization: `Bearer ${idToken}` }
    );
    return response;
  }

  /**
   * Saves or updates the user's configuration on the server.
   * @description
   * Sends a POST request to persist user configuration data.
   * If omitted, the server will use default values to initialize the config.
   * @returns A promise that resolves to the complete saved UserConfig object
   *          as returned by the server.
   *
   * @throws Re-throws any API errors after logging them for debugging purposes.
   */
  public async saveUserConfig(
    userConfig?: SaveUserConfigPayload
  ): Promise<UserConfig> {
    try {
      return await api.httpPost<UserConfig>(
        ServiceName.UserState,
        '/user-config',
        userConfig
      );
    } catch (e) {
      logger.error('Failed to save user config', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.SAVE_USER_CONFIG,
        },
      });
      throw e;
    }
  }

  /**
   * Updates flag configuration on the server.
   * Prefer using `useUserConfig().updateFlagConfig()` for automatic extension notification.
   */
  public async updateFlagConfig(
    updateFlags: Partial<IFlagsConfig>
  ): Promise<void> {
    try {
      await api.httpPatch<UserConfig>(
        ServiceName.UserState,
        '/user-config/flagsConfig',
        updateFlags
      );
    } catch (e) {
      logger.error('Failed to update flag config', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.UPDATE_FLAG_CONFIG,
        },
      });
      throw e;
    }
  }

  public async setGtmData(
    gtmIdentifiers: string,
    gtmServerPreview?: string
  ): Promise<void> {
    try {
      await api.httpPost<void>(ServiceName.UserManagement, '/gtmData', {
        identifiers: gtmIdentifiers,
        serverPreview: gtmServerPreview,
      });
    } catch (e) {
      logger.error('Failed to set GTM data', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.SET_GTM_DATA,
        },
      });
    }
  }
  /**
   * Updates group items on the server.
   * Prefer using `useUserConfig().updateGroupItems()` for automatic extension notification.
   */
  public async updateGroupItems(
    groupId: string,
    items: GroupItem[]
  ): Promise<void> {
    try {
      // the items endpoint rejects any other field, so the group endpoint carries updatedAt in the same call
      await api.httpPatch<string>(
        ServiceName.UserState,
        `/user-config/group/${groupId}`,
        { items, updatedAt: Date.now() }
      );
    } catch (e) {
      logger.error('Failed to update group items', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.UPDATE_GROUP_ITEMS,
        },
      });
    }
  }

  /**
   * Creates a new group on the server.
   * Prefer using `useUserConfig().saveNewGroup()` for placeholder-reuse and extension notification.
   */
  public async createGroup(group: Group): Promise<string> {
    try {
      return await api.httpPost<string>(
        ServiceName.UserState,
        '/user-config/group',
        group
      );
    } catch (e) {
      logger.error('Failed to create group', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.CREATE_GROUP,
        },
      });
      throw e;
    }
  }

  /**
   * Updates an existing group's fields (all but id/type) on the server.
   * Prefer using `useUserConfig().updateGroup()` for automatic extension notification.
   */
  public async updateGroup(
    groupId: string,
    group: Partial<Omit<Group, 'id' | 'type'>>
  ): Promise<string> {
    try {
      return await api.httpPatch<string>(
        ServiceName.UserState,
        `/user-config/group/${groupId}`,
        group
      );
    } catch (e) {
      logger.error('Failed to update group', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.UPDATE_GROUP,
        },
      });
      throw e;
    }
  }

  /**
   * Deletes a group on the server.
   * Prefer using `useUserConfig().deleteGroup()` for automatic extension notification.
   */
  public async deleteGroup(groupId: string): Promise<boolean> {
    try {
      return await api.httpDelete<boolean>(
        ServiceName.UserState,
        `/user-config/group/${groupId}`
      );
    } catch (e) {
      logger.error('Failed to delete group', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.DELETE_GROUP,
        },
      });
      throw e;
    }
  }

  /**
   * Saves the customized block page on the server.
   * Prefer using `useCustomizeBlockPageHook()` for automatic extension notification.
   */
  public async saveCustomizedBlockPage(
    customizedBlockPage: CustomizedBlockPagePayload,
    userId: string
  ): Promise<boolean> {
    try {
      await api.httpPut<boolean>(
        ServiceName.UserState,
        '/user-config/customizedBlockPage',
        customizedBlockPage
      );
    } catch (e) {
      logger.error('Failed to save customized block page', e, {
        tags: {
          feature: LOGGER_FEATURE.USER,
          action: LOGGER_ACTION.SAVE_CUSTOMIZED_BLOCK_PAGE,
        },
        extra: { userId: userId },
      });
      return false;
    }
    return true;
  }
}

export const apiUser = new ApiUser();
